import * as React from 'react';

import { WithId } from '@common/typescript/objects/WithId';
import { IPublished } from '@common/typescript/objects/IPublished';
import { BaseParams } from '@common/typescript/objects/BaseParams';
import LoadingWrapper, { LoadingWrapperProps } from '@common/react/components/UI/LoadingWrapper/LoadingWrapper';
import UseRequest from '@common/react/hooks/useRequest';

interface FlagSwitcherProps<
	T extends WithId,
	TRequest
> extends Omit<LoadingWrapperProps<TRequest>, 'children' | 'actionFunction'> {
	item: T;
	requestType: string;
	getFlag: (item: T) => boolean;
	getRequestProps: (item: T) => TRequest;
	checkedTitle?: string;
	uncheckedTitle?: string;
	className?: string;
	checkedClassName?: string;
	children?: React.ReactNode;
}

export const FlagSwitcher: <T extends WithId, TRequest = BaseParams>(
	props: FlagSwitcherProps<T, TRequest>,
) => React.ReactElement = <T extends WithId, TRequest = BaseParams>(
	props: FlagSwitcherProps<T, TRequest>,
) => {
	const {
		requestType,
		item,
		checkedTitle = '',
		uncheckedTitle = '',
		getFlag,
		getRequestProps,
		className = 'check-icon',
		checkedClassName = 'check-icon_checked',
		children = <i className="fa fa-check" />,
		...rest
	} = props;
	const request = UseRequest();

	return (<LoadingWrapper<TRequest>
		{...rest}
		actionFunction={() => request<TRequest, TRequest>(requestType, getRequestProps(item))}
	>
		{({ onClick, isLoading }) =>
			<div className={`text-center ${isLoading ? 'isLoading loading-component' : ''}`}>
				<span
					title={getFlag(item) ? checkedTitle : uncheckedTitle}
					className={`${className} ${getFlag(item) ? checkedClassName : ''}`}
					onClick={() => onClick()}
				>
					{children}
				</span>
			</div>}
	</LoadingWrapper>);
};

type Published = WithId & IPublished;

interface PublishSwitcherProps extends Omit<
	FlagSwitcherProps<Published, PublishSwitchRequestType>, 'getFlag' | 'getRequestProps'
> { }

interface PublishSwitchRequestType extends WithId {
	published: boolean;
}

export const PublishSwitcher: (props: PublishSwitcherProps) => React.ReactElement = (props: PublishSwitcherProps) => {
	const {
		checkedTitle = 'Published',
		uncheckedTitle = 'Not Published',
		...rest
	} = props;

	return (<FlagSwitcher<Published, PublishSwitchRequestType>
		checkedTitle={checkedTitle}
		uncheckedTitle={uncheckedTitle}
		getFlag={(item) => (item.published)}
		getRequestProps={(item) => ({ id: item.id, published: !item.published })}
		{...rest}
	/>);
};
